import React, { useMemo, useState } from 'react';
import { Button, Card, Typography, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { CoreInput } from '@/components/Form/InputAntForm';
import { getErrorMessage } from '@/components/hooks/useGetErrorMessage';
import { authService, authErrorCodes } from '@/modules/users/services/auth.service';

interface ChangePasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const getFirebaseErrorCode = (error: unknown): string | undefined => {
  if (error && typeof error === 'object' && 'code' in error) {
    return String((error as { code: string }).code);
  }
  return undefined;
};

export const ChangePasswordPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = useMemo(
    () =>
      yup.object().shape({
        oldPassword: yup.string().required(t('common.changePasswordOldRequired')),
        newPassword: yup
          .string()
          .required(t('common.changePasswordNewRequired'))
          .min(8, t('common.changePasswordNewMin'))
          .notOneOf([yup.ref('oldPassword')], t('common.changePasswordNewDifferent')),
        confirmNewPassword: yup
          .string()
          .required(t('common.changePasswordConfirmRequired'))
          .oneOf([yup.ref('newPassword')], t('common.changePasswordConfirmMatch')),
      }),
    [t, i18n.language],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ChangePasswordForm>({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: { oldPassword: '', newPassword: '', confirmNewPassword: '' },
  });

  const onSubmit = async (input: ChangePasswordForm) => {
    setIsSubmitting(true);
    try {
      await authService.changePassword(input.oldPassword, input.newPassword);
      message.success(t('common.changePasswordSuccess'));
      reset();
      navigate('/');
    } catch (error: unknown) {
      const firebaseCode = getFirebaseErrorCode(error);

      if (
        firebaseCode === authErrorCodes.wrongPassword ||
        firebaseCode === authErrorCodes.invalidCredential
      ) {
        message.error(t('common.changePasswordWrongCurrent'));
      } else if (firebaseCode === authErrorCodes.weakPassword) {
        message.error(t('common.changePasswordWeak'));
      } else {
        message.error(getErrorMessage(error) ?? t('common.changePasswordFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      key={i18n.language}
      className='flex min-h-[calc(100vh-180px)] items-center justify-center px-6 py-8'
    >
      <Card bordered={false} className='w-full max-w-md shadow-sm'>
        <div className='mb-6 text-center'>
          <LockOutlined style={{ fontSize: 32, color: '#1890ff', marginBottom: 12 }} />
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t('common.changePassword')}
          </Typography.Title>
          <Typography.Paragraph type='secondary' style={{ marginTop: 8, marginBottom: 0 }}>
            {t('common.changePasswordDescription')}
          </Typography.Paragraph>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Typography.Text className='block font-medium'>
                {t('common.changePasswordCurrent')} <span className='text-red-500'>*</span>
              </Typography.Text>
              <CoreInput
                control={control}
                name='oldPassword'
                type='password'
                placeholder={t('common.changePasswordCurrentPlaceholder')}
                autoComplete='current-password'
              />
            </div>

            <div className='space-y-2'>
              <Typography.Text className='block font-medium'>
                {t('common.changePasswordNew')} <span className='text-red-500'>*</span>
              </Typography.Text>
              <CoreInput
                control={control}
                name='newPassword'
                type='password'
                placeholder={t('common.changePasswordNewPlaceholder')}
                autoComplete='new-password'
              />
              <Typography.Text type='secondary' className='text-sm'>
                {t('common.changePasswordMinLengthHint')}
              </Typography.Text>
            </div>

            <div className='space-y-2'>
              <Typography.Text className='block font-medium'>
                {t('common.changePasswordConfirm')} <span className='text-red-500'>*</span>
              </Typography.Text>
              <CoreInput
                control={control}
                name='confirmNewPassword'
                type='password'
                placeholder={t('common.changePasswordConfirmPlaceholder')}
                autoComplete='new-password'
              />
            </div>
          </div>

          <div className='flex justify-end gap-3 pt-4 border-t'>
            <Button size='large' onClick={() => navigate('/me')} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button
              type='primary'
              htmlType='submit'
              loading={isSubmitting}
              disabled={!isDirty}
              size='large'
            >
              {t('common.changePasswordUpdate')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
